import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import { useTypedSelector } from '../redux';

export const useSignalRConnection = (hubUrl: string) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const connectionRef = useRef<HubConnection | null>(null);

  const accessToken = useTypedSelector((state) => state.auth.user.id);
  const isLoggedIn = useTypedSelector((state) => state.auth.isLoggedIn);
  const isConnected = connection?.state === HubConnectionState.Connected;
  const [previousGroup, setPreviousGroup] = useState<string | null>(null);

  const connect = useCallback(async () => {
    if (connectionRef.current) {
      return;
    }

    const newConnection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => accessToken || ''
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    try {
      await newConnection.start();
      console.log('SignalR Connected.');
      setConnection(newConnection);
      connectionRef.current = newConnection;
    } catch (error) {
      console.error('SignalR Connection Error: ', error);
    }
  }, [hubUrl, accessToken]);

  const disconnect = useCallback(async () => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
        console.log('SignalR Disconnected.');
      } catch (error) {
        console.error('SignalR Disconnection Error: ', error);
      } finally {
        setConnection(null);
        connectionRef.current = null;
      }
    }
  }, []);

  const leaveGroup = useCallback(async (groupId: string) => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.invoke('LeaveGroup', groupId);
        console.log(`Left group ${groupId}`);
      } catch (error) {
        console.error(`Error leaving group ${groupId}: `, error);
      }
    }
  }, []);

  const joinGroup = useCallback(
    async (groupId: string) => {
      if (previousGroup !== null) {
        await leaveGroup(previousGroup);
      }

      if (connectionRef.current) {
        try {
          await connectionRef.current.invoke('JoinGroup', groupId);
          setPreviousGroup(groupId);
          console.log(`Joined group ${groupId}`);
        } catch (error) {
          console.error(`Error joining group ${groupId}: `, error);
        }
      }
    },
    [leaveGroup, previousGroup]
  );

  useEffect(() => {
    if (!isLoggedIn || !accessToken) {
      disconnect();
      return;
    }

    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect, isLoggedIn, accessToken]);

  return {
    connection,
    isConnected,
    joinGroup,
    leaveGroup
  };
};
